import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty } from 'antd';
import moment from 'moment';

import { ExpenseStatusControl } from 'molecules';

import * as S from '../PaymentCalendar.styled';
import {
  TCalendarDay,
  TCalendarRow,
  PaymentChip,
  IncomeStateChip,
  STATUS,
  fmtMoney,
} from '../shared';
import { ReschedulePopover } from './ReschedulePopover';

export interface IToPayListProps {
  /** Days in scope; their expense rows are flattened into the to-pay queue. */
  days: TCalendarDay[];
  /** Called after a successful reschedule (refetch). */
  onMoved?: () => void;
  /** Mark a row as paid (the prominent quick button). */
  onMarkPaid: (row: TCalendarRow) => void;
  /** Set a row to any status (the inline chip-menu). */
  onSetStatus: (row: TCalendarRow, status: string) => void;
}

const DATE_FMT = 'YYYY-MM-DD';

/** Window (days) within which an upcoming payment is flagged "due soon". */
const DUE_SOON_DAYS = 3;

interface IToPayItem {
  row: TCalendarRow;
  overdue: boolean;
  dueSoon: boolean;
}

const rows = (
  list: ReadonlyArray<TCalendarRow | null> | null | undefined,
): TCalendarRow[] => (list || []).filter(Boolean) as TCalendarRow[];

/**
 * The "to-pay" queue: every unpaid expense row across the scope, overdue first
 * then by date ascending. Each item carries an overdue / due-soon flag for the
 * badge, a status chip, a mark-paid action and a reschedule popover.
 */
export const ToPayList = memo<IToPayListProps>(
  ({ days, onMoved, onMarkPaid, onSetStatus }) => {
    const { t } = useTranslation();

    const items = useMemo<IToPayItem[]>(() => {
      const today = moment().startOf('day');
      const todayIso = today.format(DATE_FMT);

      return days
        .flatMap((d) => rows(d.expenseRows))
        .filter(
          (r) =>
            r.status !== STATUS.paid &&
            r.status !== STATUS.cancelled &&
            // Backend correction/chargeback rows are POSITIVE expenses with
            // status='paid' — the status filter above already drops them. The
            // sign guard below is for negative Fakturownia-mirrored correction
            // invoices (money back): they don't belong in the «К оплате» queue
            // with an «Оплатить» button.
            Number(r.amount ?? 0) >= 0,
        )
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        .map((r) => {
          const date = r.date || '';
          const overdue = !!date && date < todayIso;
          const dueSoon =
            !overdue &&
            !!date &&
            moment(date, DATE_FMT).diff(today, 'days') <= DUE_SOON_DAYS;
          return { row: r, overdue, dueSoon };
        })
        .sort((a, b) => {
          // Overdue first, then by date ascending.
          if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
          return (a.row.date || '').localeCompare(b.row.date || '');
        });
    }, [days]);

    return (
      <div>
        <S.SectionTitle>
          {t('pages.paymentCalendar.toPay.title')}
        </S.SectionTitle>
        {items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.paymentCalendar.toPay.empty')}
          />
        ) : (
          <S.ChipStack>
            {items.map(({ row, overdue, dueSoon }) => (
              <PaymentChip
                key={row.sourceRef || `${row.title}-${row.date}`}
                tone={overdue ? 'danger' : row.toneAfter}
              >
                <span>{row.date ? moment(row.date).format('DD.MM') : '—'}</span>
                <span>{row.title}</span>
                {overdue && (
                  <IncomeStateChip state="overdue">
                    {t('pages.paymentCalendar.toPay.overdue')}
                  </IncomeStateChip>
                )}
                {dueSoon && (
                  <IncomeStateChip state="awaited">
                    {t('pages.paymentCalendar.toPay.dueSoon')}
                  </IncomeStateChip>
                )}
                <ExpenseStatusControl
                  value={row.status}
                  onChange={(s) => onSetStatus(row, s)}
                />
                <S.ChipActions>
                  <span>{fmtMoney(row.amountPln)}</span>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => onMarkPaid(row)}
                  >
                    {t('pages.paymentCalendar.toPay.markPaid')}
                  </Button>
                  <ReschedulePopover row={row} onMoved={onMoved}>
                    <Button size="small">
                      {t('pages.paymentCalendar.reschedule.title')}
                    </Button>
                  </ReschedulePopover>
                </S.ChipActions>
              </PaymentChip>
            ))}
          </S.ChipStack>
        )}
      </div>
    );
  },
);
